<script lang="ts">
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import Alert, { showAlert } from '$lib/Alert.svelte';
	import googleLogo from '$lib/assets/external/google-logo.svg';
	import Button from '$lib/Button.svelte';
	import ButtonSubmit from '$lib/ButtonSubmit.svelte';
	import Input from '$lib/Input.svelte';
	import { schema } from '$lib/schemas/signup';
	import { signInWithRedirect, signUp } from 'aws-amplify/auth';
	import { Buffer } from 'buffer';
	import { defaults, superForm } from 'sveltekit-superforms';
	import { zod } from 'sveltekit-superforms/adapters';

	const { form, errors, enhance, allErrors, submitting, delayed } = superForm(
		defaults(zod(schema)),
		{
			SPA: true,
			validators: zod(schema),
			dataType: 'json',
			resetForm: false,
			async onUpdate({ form }) {
				if (form.valid) {
					const { nickname, email, password } = form.data;
					try {
						const { nextStep } = await signUp({
							username: email,
							password,
							options: {
								userAttributes: {
									nickname
								}
							}
						});

						switch (nextStep.signUpStep) {
							case 'CONFIRM_SIGN_UP':
								await goto(`/verify/${Buffer.from(email).toString('base64url')};`);
								showAlert({
									data: {
										status: 'success',
										message: 'Account created successfully. Please verify your email.'
									}
								});
								break;
						}
					} catch (error) {
						showAlert({
							data: {
								status: 'error',
								message:
									error instanceof Error
										? error.message
										: 'We couldn’t process your request. Please try again.'
							}
						});
						scrollTo(0, 0);
					}
				}
			}
		}
	);
</script>

<Alert />
<h1 class="h1-serif mb-3 text-center">Create free account</h1>
<p class="mb-8 text-center">Track your favourite stocks easily with Beansprout</p>

<p class="small-bold mb-5 text-center">
	By creating an account, you agree to the Beansprout<br />
	<a href="/terms-of-use" class="link link-primary">Terms of use</a>
	and <a href="/privacy" class="link link-primary">Privacy Policy</a>.
</p>

<Button
	variant="ghost"
	size="large"
	class="flex w-full items-center justify-center"
	click={() => {
		const follow = page.url.searchParams.get('follow');
		let customState = page.state.from;
		if (follow) {
			customState = `/quote/${follow}?following=true`;
		}
		signInWithRedirect({
			provider: 'Google',
			customState
		});
	}}
>
	<img src={googleLogo} alt="google logo" />
	<span class="ml-6">Continue with Google</span>
</Button>
<div class="divider">
	<span class="small-bold my-auto px-2">or</span>
</div>
<form use:enhance class="flex flex-col gap-5">
	<Input name="nickname" label="Name" bind:value={$form.nickname} errors={$errors.nickname} />
	<Input type="email" name="email" label="Email" bind:value={$form.email} errors={$errors.email} />
	<Input
		type="password"
		name="password"
		label="Password"
		bind:value={$form.password}
		errors={$errors.password}
	/>
	<Input
		type="password"
		name="confirmPassword"
		label="Confirm Password"
		bind:value={$form.confirmPassword}
		errors={$errors.confirmPassword}
	/>

	<ButtonSubmit disabled={$allErrors.length > 0} submitting={$submitting} delayed={$delayed}
		>Create free account</ButtonSubmit
	>
</form>
<p class="index-bold mt-6 text-center">
	Already have an account?
	{#if page.url.pathname !== '/signup'}
		<button
			onclick={() =>
				goto('/login', {
					state: page.state
				})}
			class="link link-primary">Sign in</button
		>
	{:else}
		<a href="/login" class="link link-primary">Sign in</a>
	{/if}
</p>

<style lang="postcss">
	@reference '../../../app.css';

	.divider {
		@apply my-6 flex items-center;

		&:before,
		&:after {
			content: '';
			@apply bg-gray-light h-[1px] w-[50%];
		}
	}
</style>
